import { loadStripe, Stripe } from '@stripe/stripe-js'
import axios from 'axios'

import { config } from '@/config'

export const api = axios.create({
  baseURL: 'https://api.stripe.com/v1/',
})

type CreditCard = {
  number: string
  name: string
  cvc: string
  expMonth: string
  expYear: string
}
function createTokenWithCard({
  number,
  name,
  cvc,
  expMonth,
  expYear,
}: CreditCard) {
  const query = new URLSearchParams()
  query.append('card[number]', number)
  query.append('card[name]', name)
  query.append('card[cvc]', cvc)
  query.append('card[exp_month]', expMonth)
  query.append('card[exp_year]', expYear)

  return api
    .post('/tokens', query)
    .then(res => {
      return res.data.id
    })
    .catch(() => {
      throw new Error('createTokenWithCardError')
    })
}

/**
 * カード登録の3Dセキュア認証を実行する
 * @param clientSecret 認証するSetupIntentのclientSecret
 */
const confirmCardSetupWithStripe = (clientSecret: string) =>
  withStripe(stripe => stripe.confirmCardSetup(clientSecret))

/**
 * 決済の3Dセキュア認証を実行する
 * 認証だけを行い決済は確定しないため、確定はサーバ側で行う
 * @param clientSecret 認証するPaymentIntentのclientSecret
 */
const handleCardActionWithStripe = (clientSecret: string) =>
  withStripe(stripe => stripe.handleCardAction(clientSecret))

/**
 * 請求書の決済の3Dセキュア認証を実行する
 * 認証とあわせて決済まで完了する
 * @param clientSecret 認証するPaymentIntentのclientSecret
 */
const handleNextActionWithStripe = (clientSecret: string) =>
  withStripe(stripe => stripe.handleNextAction({ clientSecret }))

/**
 * Stripeを読み込み、モーダルのフォーカス制御を止めた状態で処理を実行する
 * @param action Stripeを使って実行する処理
 */
const withStripe = async <T>(action: (stripe: Stripe) => Promise<T>) => {
  const stripe = await loadStripe(config.stripeApiKey)
  if (!stripe) throw new Error('Stripe failed to initialize')

  const restoreModalFocusTrap = suspendModalFocusTrap()
  try {
    return await action(stripe)
  } finally {
    restoreModalFocusTrap()
  }
}

/**
 * モーダルのフォーカス制御を止めて、Stripeの認証画面を操作できるようにする
 * IonicとMUIはどちらもdocumentでフォーカスを待ち受けて奪い返すため、captureで先に走るwindowで伝播を止める
 * @returns フォーカス制御を元に戻す関数
 */
const suspendModalFocusTrap = () => {
  const stopFocusOnIframe = (event: FocusEvent) => {
    if ((event.target as HTMLElement)?.tagName !== 'IFRAME') return
    event.stopImmediatePropagation()
  }
  window.addEventListener('focus', stopFocusOnIframe, true)
  window.addEventListener('focusin', stopFocusOnIframe, true)

  return () => {
    window.removeEventListener('focus', stopFocusOnIframe, true)
    window.removeEventListener('focusin', stopFocusOnIframe, true)
  }
}

export {
  confirmCardSetupWithStripe,
  createTokenWithCard,
  handleCardActionWithStripe,
  handleNextActionWithStripe,
}
